export type LandingExperienceAnalytics = {
  category: string
  action: string
  properties?: Record<string, unknown>
}

export type LandingExperienceAction = {
  intent: string
  title: string
  analytics?: LandingExperienceAnalytics
}

export type LandingExperienceBanner = {
  title: string
  subtitle: string
  expiry_date?: string
  expiry_label?: string
}

export type LandingExperienceHero = {
  welcome: string
  welcomeMobile: string
  headline: string
  subtitle: string
}

export type LandingExperienceTagStyle = {
  index: number
  highlighted?: boolean
  strong_text?: string[]
}

export type LandingExperienceOption = {
  id: string
  title: string
  summary: string
  tags: string[]
  tag_styles?: LandingExperienceTagStyle[]
  action: LandingExperienceAction
}

export type LandingExperienceData = {
  heading: string
  hero: LandingExperienceHero
  banner?: LandingExperienceBanner | null
  options: LandingExperienceOption[]
  defer: LandingExperienceAction
}

export type LandingExperienceResponse = {
  status: number
  data: LandingExperienceData | null
}

// Snowplow event descriptor the xAPI attaches to a trackable action. Distinct from
// the contextual OrderAnalytics below (which is order metadata, not an event).
export type DashboardAnalytics = {
  category: string
  action: string
  properties?: Record<string, unknown>
}

export type DashboardHeaderTagType = 'positive' | 'neutral'

export type DashboardHeaderTag = {
  text: string
  type: DashboardHeaderTagType
}

export type PageHeaderData = {
  title: string
  subtitle: DashboardHeaderTag
}

export type DashboardActionRef = {
  intent: string
  title: string
  analytics?: DashboardAnalytics
}

export type UnsubscribedHeroOption = {
  title: string
  summary: string
  icon: string
  badge?: string
  action: DashboardActionRef
}

export type DashboardOffer = {
  badge: string
  label?: string
  expiry_label?: string
}

export type UnsubscribedHeroData = {
  welcome: string
  headline: string
  offer?: DashboardOffer
  options: UnsubscribedHeroOption[]
}

export type UnsubscribedHeroV2Data = {
  welcome: string
  headline: string
  tags: string[]
  offer?: DashboardOffer
  cta: {
    title: string
    intent: string
    analytics?: DashboardAnalytics
  }
}

export type RecipeAction = {
  intent: string
  // Present for recipes that deep-link to a destination (e.g. a past order's cookbook page).
  // Absent for order-scoped intents like `edit_recipes`, which route via the order id instead.
  recipe_url?: string
  analytics?: DashboardAnalytics
}

export type OrderRecipe = {
  id: string
  image_url: string
  accessibility_label: string
  action?: RecipeAction
}

export type OrderPrice = {
  kind: 'regular' | 'discounted'
  amount: string
  original?: string
}

export type OrderAnalytics = {
  order_id: string
  order_state: string
  order_phase: string
  delivery_day_id: string
  delivery_slot_id: string
  portion_size: string
  period_id: string
  delivery_date: string
  cut_off_date: string
}

export type OrderStateType = 'positive' | 'neutral' | 'warning' | 'negative'

export type OrderDeliverySlot = {
  id: string
  day_slot_lead_time_id: string
  time_label: string
}

export type OrderDeliveryDay = {
  id: string
  formatted_date: string
  slots: OrderDeliverySlot[]
}

export type OrderDetailAction = {
  title: string
  error_message?: string
  analytics?: DashboardAnalytics
}

export type OrderDetails = {
  heading: string
  delivery_date_label: string
  delivery_time_label: string
  delivery_address_title?: string
  delivery_address_label?: string
  selected_day_id?: string
  selected_slot_id?: string
  available_delivery_days?: OrderDeliveryDay[]
  edit_slot_action?: OrderDetailAction
  skip_action?: OrderDetailAction
}

export type OrderSectionData = {
  id: string
  heading?: string
  title: string
  state_title: string
  state_type: OrderStateType
  summary_text: string
  edit_until_label?: string
  recipes: OrderRecipe[]
  price: OrderPrice
  primary_action?: DashboardActionRef
  help_cta?: { title: string; intent: string; analytics?: DashboardAnalytics } | null
  details: OrderDetails
  analytics?: OrderAnalytics
}

export type OrderBoxData = {
  title: string
  tags: string[]
  offer?: DashboardOffer
  cta: {
    title: string
    intent: string
    analytics?: DashboardAnalytics
  }
}

export type RecipeCarouselRecipeData = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  cookTimeMins?: number
  kcal?: number
}

export type RecipeCarouselSectionData = {
  title: string
  subtitle?: string
  recipes: RecipeCarouselRecipeData[]
  action: {
    title: string
    intent: string
    collectionSlug: string
    analytics?: DashboardAnalytics
  }
}

export type HeatAndEatCarouselMealData = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  cookTimeMins?: number
  kcal?: number
  // Per-meal tap descriptor; carries this meal's recipe_id (server-attached).
  analytics?: DashboardAnalytics
}

export type HeatAndEatCarouselAction = {
  title: string
  intent: string
  collectionSlug: string
  analytics?: DashboardAnalytics
}

export type HeatAndEatCarouselSecondaryAction = {
  title: string
  intent: string
  collectionSlug: string
  orderId: string
  analytics?: DashboardAnalytics
}

export type HeatAndEatCarouselSectionData = {
  title: string
  subtitle?: string
  tag: string
  meals: HeatAndEatCarouselMealData[]
  action: HeatAndEatCarouselAction
  // Fired on mount; carries home_page_state and add_to_box_cta_shown.
  displayAnalytics?: DashboardAnalytics
  // Present only when the caller has an upcoming uncommitted box.
  secondaryAction?: HeatAndEatCarouselSecondaryAction
}

export type DashboardActionData = {
  title: string
  subtitle: string
  icon: string
  intent: string
  analytics?: DashboardAnalytics
}

export type DashboardActionCompactData = {
  title: string
  intent: string
}

export type DashboardSection =
  | { type: 'unsubscribed_hero'; data: UnsubscribedHeroData }
  | { type: 'unsubscribed_hero_v2'; data: UnsubscribedHeroV2Data }
  | { type: 'page_header'; data: PageHeaderData }
  | { type: 'order'; data: OrderSectionData }
  | { type: 'order_box'; data: OrderBoxData }
  | { type: 'recipe_carousel'; data: RecipeCarouselSectionData }
  | { type: 'heat_and_eat_carousel'; data: HeatAndEatCarouselSectionData }
  | { type: 'dashboard_action'; data: DashboardActionData }
  | { type: 'dashboard_action_compact'; data: DashboardActionCompactData }

// Which composition the dashboard returned (mirrors the pageLoad `home_page_state`). 'no_order'
// is the empty state clients gate on — 'post_order' (delivered box, nothing booked) is NOT.
export const DASHBOARD_EXPERIENCE_STATE = {
  noOrder: 'no_order',
  pendingOrder: 'pending_order',
  postOrder: 'post_order',
} as const

export type DashboardExperienceState =
  typeof DASHBOARD_EXPERIENCE_STATE[keyof typeof DASHBOARD_EXPERIENCE_STATE]

export type DashboardExperienceData = {
  state?: DashboardExperienceState
  sections: DashboardSection[]
}

// Load-time tracking events the xAPI attaches alongside the dashboard data. Every
// level is optional so a missing block is a safe no-op rather than a parse error.
export type DashboardExperienceMeta = {
  trackingEvents?: {
    pageLoad?: DashboardAnalytics
  }
}

export type DashboardExperienceResponse = {
  status: number
  data: DashboardExperienceData | null
  pageLoad?: DashboardAnalytics
}
